import { useEffect, useState } from "react";
import { format } from "date-fns";
import { User, Flag, Lightbulb, Instagram, Youtube, Globe, Link2, Clock, CalendarPlus, ListChecks } from "lucide-react";
import { Card } from "@/components/ui/card";
import { cn } from "@/lib/utils";
import { ASPECT_LABEL, STATUS_META } from "./topicCollection";

const PRIORITY = { low: "text-muted-foreground", normal: "text-primary", high: "text-warning" };
const fdate = (iso) => { try { return format(new Date(iso), "d MMM yyyy"); } catch { return ""; } };
const PLATFORM_ICON = { Instagram, YouTube: Youtube, Web: Globe, Other: Link2 };

// Fewer chips on narrow screens (spec §7) while desktop/tablet caps stay as approved.
function useChipCaps() {
  const [mobile, setMobile] = useState(typeof window !== "undefined" && window.innerWidth < 640);
  useEffect(() => {
    const on = () => setMobile(window.innerWidth < 640);
    window.addEventListener("resize", on);
    return () => window.removeEventListener("resize", on);
  }, []);
  return { platforms: mobile ? 2 : 4, aspects: mobile ? 2 : 3 };
}

const platformLine = (platforms) =>
  Object.entries(platforms || {}).sort((a, b) => b[1] - a[1]).map(([p, n]) => `${p} ×${n}`);
const aspectLine = (aspects) =>
  Object.entries(aspects || {}).sort((a, b) => b[1] - a[1])
    .map(([a, n]) => (n > 1 ? `${ASPECT_LABEL[a] || a} ×${n}` : ASPECT_LABEL[a] || a));

// Reusable, aligned detailed reference row (spec §6): platform icon + title,
// never a raw URL when a title exists; long titles wrap without shifting siblings.
function RefItem({ item }) {
  const Icon = PLATFORM_ICON[item.platform] || Link2;
  return (
    <div className="flex min-w-0 items-start gap-1.5 text-[12px] text-muted-foreground">
      <Icon className="mt-0.5 h-3.5 w-3.5 shrink-0" />
      <span className="min-w-0 flex-1 break-words">{item.title || `${item.platform} link`}</span>
    </div>
  );
}

// Canonical Topic card. All densities share ONE min-w-0 content column with a
// single vertical rhythm (space-y) so every block aligns to the same left inset
// (spec §2, §8, §17). Density changes WHAT is shown and the spacing scale only.
export function TopicCard({ t, cfg, onOpen }) {
  const caps = useChipCaps();
  const hidden = new Set(cfg.hidden || []);
  const density = cfg.density || "comfortable";
  const show = (k) => !hidden.has(k);
  const insp = t.inspiration || { count: 0, platforms: {}, aspects: {}, top: [] };

  const pad = density === "compact" ? "p-3" : density === "detailed" ? "p-4 sm:p-5" : "p-4";
  const gap = density === "compact" ? "space-y-1.5" : density === "detailed" ? "space-y-3" : "space-y-2.5";
  const showSummary = show("summary") && density !== "compact" && !!t.summary;
  const showInspBlock = insp.count > 0 && density !== "compact";
  const showLinks = show("links") && density === "detailed" && insp.top?.length > 0;
  const showDates = (show("updated") || show("created")) && density !== "compact";

  const plats = show("platforms") ? platformLine(insp.platforms).slice(0, caps.platforms) : [];
  const asps = show("aspects") ? aspectLine(insp.aspects).slice(0, caps.aspects) : [];

  return (
    <Card onClick={() => onOpen(t.id)} data-testid={`topic-card-${t.id}`}
      className={cn("group cursor-pointer overflow-hidden transition-colors hover:border-accent/50 hover:bg-muted/30", pad)}>
      <div className={cn("min-w-0", gap)}>
        {/* title row */}
        <div className="flex items-start justify-between gap-3">
          <h3 className="min-w-0 flex-1 break-words text-base font-medium leading-snug text-foreground transition-colors line-clamp-2 group-hover:text-primary" data-testid="topic-card-title">{t.title}</h3>
          {t.archived && <span className="mt-0.5 shrink-0 rounded-full border border-border/60 px-2 py-0.5 text-[11px] font-medium uppercase tracking-wide text-muted-foreground">Archived</span>}
        </div>

        {showSummary && <p className="break-words text-sm leading-relaxed text-muted-foreground line-clamp-2">{t.summary}</p>}

        {/* primary metadata — one wrapping row aligned to the column inset */}
        <div className="flex flex-wrap items-center gap-x-4 gap-y-1.5 text-[12px] text-muted-foreground">
          {show("status") && (() => { const sm = STATUS_META[t.status] || STATUS_META.idea; return (
            <span className={cn("inline-flex items-center rounded-full px-2 py-0.5 text-[11px] font-medium", sm.cls)}>{sm.label}</span>); })()}
          {show("readiness") && t.readiness && (t.readiness.started ? (
            <span data-testid={`topic-readiness-${t.id}`} className={cn("inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-medium",
              t.readiness.ready ? "bg-success/10 text-success" : t.readiness.blocking > 0 ? "bg-warning/10 text-warning" : "bg-muted text-muted-foreground")}>
              <ListChecks className="h-3 w-3 shrink-0" />
              {t.readiness.ready ? `Ready · ${t.readiness.complete}/${t.readiness.total}` : t.readiness.blocking > 0 ? `${t.readiness.blocking} to do · ${t.readiness.complete}/${t.readiness.total}` : `${t.readiness.complete}/${t.readiness.total}`}
            </span>
          ) : (
            <span data-testid={`topic-readiness-${t.id}`} className="inline-flex items-center gap-1 rounded-full bg-muted px-2 py-0.5 text-[11px] font-medium text-muted-foreground"><ListChecks className="h-3 w-3 shrink-0" /> Not started</span>
          ))}
          {t.review?.needs_my_review && (
            <span className="inline-flex items-center rounded-full bg-warning/10 px-2 py-0.5 text-[11px] font-medium text-warning" data-testid={`topic-needs-review-${t.id}`}>Needs your review</span>
          )}
          {t.status === "under_review" && t.review && !t.review.needs_my_review && (
            <span className="text-[11px] text-muted-foreground">{t.review.approved}/{t.review.required} approved</span>
          )}
          {show("priority") && <span className={cn("inline-flex items-center gap-1 capitalize", PRIORITY[t.priority])}><Flag className="h-3.5 w-3.5 shrink-0" /> {t.priority || "normal"}</span>}
          {show("owner") && t.owner_name && <span className="inline-flex min-w-0 items-center gap-1"><User className="h-3.5 w-3.5 shrink-0" /> <span className="truncate">{t.owner_name}</span></span>}
          {density === "compact" && insp.count > 0 && show("inspiration_count") && (
            <span className="inline-flex items-center gap-1 text-accent"><Lightbulb className="h-3.5 w-3.5 shrink-0" /> {insp.count}</span>
          )}
        </div>

        {/* inspiration summary — its own aligned section */}
        {showInspBlock && (show("inspiration_count") || plats.length > 0 || asps.length > 0) && (
          <div className="space-y-1.5" data-testid={`topic-insp-summary-${t.id}`}>
            <div className="flex flex-wrap items-center gap-x-2 gap-y-1 text-[12px]">
              {show("inspiration_count") && (
                <span className="inline-flex items-center gap-1 font-medium text-foreground/80"><Lightbulb className="h-3.5 w-3.5 shrink-0 text-accent" /> {insp.count} {insp.count === 1 ? "Inspiration" : "Inspirations"}</span>
              )}
              {plats.length > 0 && <span className="min-w-0 break-words text-muted-foreground">{plats.join(" · ")}</span>}
            </div>
            {asps.length > 0 && (
              <div className="flex flex-wrap gap-1.5" data-testid={`topic-insp-aspects-${t.id}`}>
                {asps.map((a) => <span key={a} className="rounded-full border border-border/60 px-2 py-0.5 text-[11px] leading-5 text-muted-foreground">{a}</span>)}
              </div>
            )}
            {showLinks && (
              <div className="space-y-1" data-testid={`topic-insp-links-${t.id}`}>
                {insp.top.map((l, i) => <RefItem key={i} item={l} />)}
                {insp.count > insp.top.length && <div className="pl-5 text-[11px] text-muted-foreground/70">+{insp.count - insp.top.length} more</div>}
              </div>
            )}
          </div>
        )}

        {/* dates */}
        {showDates && (
          <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-[11px] text-muted-foreground/80">
            {show("updated") && <span className="inline-flex items-center gap-1"><Clock className="h-3 w-3 shrink-0" /> Updated {fdate(t.updated_at)}</span>}
            {show("created") && <span className="inline-flex items-center gap-1"><CalendarPlus className="h-3 w-3 shrink-0" /> Created {fdate(t.created_at)}</span>}
          </div>
        )}
      </div>
    </Card>
  );
}
